import { useContext, useEffect, useState } from "react";
import {
  getWidgets,
  createWidget,
  updateWidget,
  deleteWidget,
} from "../services/configurations/widgets";
import { Widget } from "../interfaces";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS" | "DATAWITHOUTAPPID";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

const handleWidgetActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createWidget,
        fnMsg: t("Widgets.apiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateWidget,
        fnMsg: t("Widgets.apiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteWidget,
        fnMsg: t("Widgets.apiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useWidgets = (type: UseType) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<Widget[] | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || '0';

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const widgetAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleWidgetActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if ((type === "DATA" && appid) || (type === "DATAWITHOUTAPPID" && appid !== '0')) {
      (async function () {
        await Promise.resolve(getWidgets(appid))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type]);

  return {
    data,
    widgetAction,
    reloadFn,
  };
};
